import React, { useContext, useEffect, useMemo, useRef } from 'react';
import { createTheme, ThemeProvider, useTheme } from '@mui/material';
import { FilterBarContext, FilterFieldName } from './FilterBarContext';
import { cn } from '@/lib/utils/utils';

interface EmbeddedFilterItemProps {
  filterName: FilterFieldName;
  filter: React.ReactNode;
  /** Trigger a search as soon as the filter value changes, instead of waiting for the search button. */
  searchOnChange?: boolean;
  /** Take the current value change into account without searching, used when the filter bar searches itself (e.g. on reset). */
  suppressSearchOnChange?: boolean;
  className?: string;
}

/**
 * Renders a filter directly in the filter bar instead of behind a dropdown button,
 * so its input (e.g. a location or asset search box) is visible right away.
 */
const EmbeddedFilterItem = ({ filterName, filter, searchOnChange, suppressSearchOnChange, className }: EmbeddedFilterItemProps) => {
  const { getFilterValue, triggerSearch } = useContext(FilterBarContext);
  const theme = useTheme();

  // The filter bar is a compact horizontal bar, so render the inputs in their dense variant.
  const compactTheme = useMemo(
    () =>
      createTheme(theme, {
        components: {
          MuiTextField: { defaultProps: { size: 'small' } },
          MuiAutocomplete: { defaultProps: { size: 'small' } },
          MuiFormControl: { defaultProps: { size: 'small' } },
        },
      }),
    [theme],
  );

  const valueKey = JSON.stringify(getFilterValue(filterName) ?? null);
  const previousValueKeyRef = useRef(valueKey);

  useEffect(() => {
    if (previousValueKeyRef.current === valueKey) {
      return;
    }
    previousValueKeyRef.current = valueKey;

    if (searchOnChange && !suppressSearchOnChange) {
      triggerSearch();
    }
  }, [valueKey]);

  return (
    <div className={cn('shrink-0 w-56 py-1', className)}>
      <ThemeProvider theme={compactTheme}>{filter}</ThemeProvider>
    </div>
  );
};

export default EmbeddedFilterItem;
